import type { UseWarmTransferStateReturn } from './index.types';
import { CallManager } from 'Modules/softphone/services/CallManager';
import {
  selectWarmTransferConsultation,
  useSoftphoneStore,
} from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import {
  cancelWarmTransfer,
  completeWarmTransfer,
} from 'Modules/softphone/utils/warmTransfer';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

const logger = createLogger('useWarmTransferState');

/**
 * Hook for monitoring and managing warm transfer state during consultation.
 *
 * Subscribes to the warm transfer context and active calls from the store,
 * monitors lifecycle events (original call ended, consultation call ended),
 * derives UI state (consultation connected/ringing), and exposes handlers
 * for completing or cancelling the warm transfer.
 *
 * @returns {UseWarmTransferStateReturn} Warm transfer state and handler functions.
 */
export const useWarmTransferState = (): UseWarmTransferStateReturn => {
  const warmTransferContext = useSoftphoneStore((s) => s.warmTransferContext);
  const activeCalls = useSoftphoneStore((s) => s.activeCalls);
  const isCompletingTransfer = useSoftphoneStore((s) => s.isCompletingTransfer);
  const consultation = useSoftphoneStore(
    useShallow(selectWarmTransferConsultation)
  );
  const callManager = CallManager.getInstance();

  /**
   * Monitor warm transfer lifecycle:
   * - If original call ends during consultation → hang up consultation, clear context
   * - If consultation call disappears from activeCalls → clear context
   */
  useEffect(() => {
    if (!warmTransferContext) return;

    const { originalCallId, consultationCallId } = warmTransferContext;

    // Monitor: if original call is terminated by remote party during consultation
    const originalCall = activeCalls.get(originalCallId);
    if (originalCall && (originalCall.state === 'ended' || originalCall.state === 'failed')) {
      logger.info('Original call terminated during warm transfer — ending consultation', {
        originalCallId,
        consultationCallId,
      });

      // Remote party hung up — end consultation and clean up
      if (consultationCallId) {
        void callManager.hangUp(consultationCallId);
      }
      useSoftphoneStore.getState().clearWarmTransferContext();
      return;
    }

    // Monitor: if consultation call ends (recipient hung up, no answer, busy)
    if (consultationCallId && !activeCalls.has(consultationCallId)) {
      logger.info('Consultation call ended — clearing warm transfer context', {
        originalCallId,
        consultationCallId,
      });

      // Consultation ended — return to original call
      // switchToLongestHeldCall already handled by CallManager.hangUp
      useSoftphoneStore.getState().clearWarmTransferContext();
    }
  }, [warmTransferContext, activeCalls, callManager]);

  return {
    isConsultationAnswered: consultation.isAnswered,
    isCompletingTransfer,
    isWarmTransferConsultationActive: consultation.isActive,
    handleCompleteWarmTransfer: completeWarmTransfer,
    handleCancelWarmTransfer: cancelWarmTransfer,
  };
};
